import { useRouteState } from '../routeState'
import { roomFetch } from '../roomApi'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useRefreshGuard } from '../refreshGuards'
import {
  Check,
  Copy,
  Download,
  History,
  LoaderCircle,
  Plus,
  Trash2,
} from 'lucide-react'
import type { DraftRevision, SavedDraft, StoryProject } from '../types'
import { normalizeProject } from '../model'
import { compareDrafts, type DraftDifference } from '../draftComparison'
import { downloadFullProjectFile } from '../projectTransfer'
import { WorkspaceDialog } from './WorkspaceDialog'

type Props = {
  project: StoryProject
  userId: string
  onClose: () => void
  onCreate: (name: string) => Promise<DraftRevision>
  onRestore: (project: StoryProject) => Promise<void>
  onUnauthorized: () => void
}

export function DraftHistory({
  project,
  userId,
  onClose,
  onCreate,
  onRestore,
  onUnauthorized,
}: Props) {
  const [revisions, setRevisions] = useState<DraftRevision[]>([])
  const [selectedId, setSelectedId] = useRouteState<string | null>('draft', null)
  const [saved, setSaved] = useState<SavedDraft | null>(null)
  const [name, setName] = useState('')
  const [filter, setFilter] = useRouteState<'all' | 'named' | 'automatic'>('draft-kind', 'all', ['all', 'named', 'automatic'])
  const [busy, setBusy] = useState(false)
  useRefreshGuard(busy || !!name.trim(), 'Finish saving your named draft before refreshing.')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const generation = useRef(0)
  const root = '/api/projects/' + encodeURIComponent(project.id) + '/revisions'
  const comparison = useMemo(
    () => (saved ? compareDrafts(saved.project, project) : null),
    [saved, project],
  )
  const date = (value: string) =>
    new Date(value).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })

  const request = async <T,>(path = '', options?: RequestInit): Promise<T> => {
    const response = await roomFetch(userId, root + path, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options,
    })
    if (response.status === 401) {
      onUnauthorized()
      throw new Error('Sign in again to open saved drafts.')
    }
    const data = await response.json()
    if (!response.ok)
      throw new Error(
        data.error ||
          'Saved drafts are unavailable. Try again when the server is connected.',
      )
    return data as T
  }

  const load = async () => {
    const token = ++generation.current
    setLoading(true)
    setError('')
    try {
      const result = await request<{ revisions: DraftRevision[] }>()
      if (token !== generation.current) return
      setRevisions(result.revisions)
      setSelectedId((current) =>
        result.revisions.some((item) => item.id === current)
          ? current
          : (result.revisions[0]?.id ?? null),
      )
    } catch (error) {
      if (token === generation.current)
        setError(
          error instanceof Error
            ? error.message
            : 'Saved drafts are unavailable.',
        )
    } finally {
      if (token === generation.current) setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    return () => {
      generation.current++
    }
  }, [project.id])
  useEffect(() => {
    let cancelled = false
    setSaved(null)
    setConfirmDelete(false)
    if (!selectedId) return
    void request<SavedDraft>('/' + encodeURIComponent(selectedId))
      .then((value) => {
        if (!cancelled)
          setSaved({ ...value, project: normalizeProject(value.project) })
      })
      .catch((error) => {
        if (!cancelled)
          setError(
            error instanceof Error
              ? error.message
              : 'This saved draft could not be read.',
          )
      })
    return () => {
      cancelled = true
    }
  }, [selectedId])

  const act = async (operation: () => Promise<void>) => {
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await operation()
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : 'The operation could not be completed.',
      )
    } finally {
      setBusy(false)
    }
  }

  const renderFields = (fields: DraftDifference[]) =>
    fields.map((field) => (
      <details className="draft-field" key={field.label}>
        <summary>{field.label}</summary>
        <div className="draft-diff">
          <div>
            <span>Saved draft</span>
            <pre>{field.before || '(empty)'}</pre>
          </div>
          <div>
            <span>Current draft</span>
            <pre>{field.after || '(empty)'}</pre>
          </div>
        </div>
      </details>
    ))

  return (
    <WorkspaceDialog
      title="Draft history"
      eyebrow="MAKE ROOM FOR ANOTHER VERSION"
      onClose={() => {
        if (!busy) onClose()
      }}
      className="draft-history-dialog"
    >
      <form
        className="name-draft-form"
        onSubmit={(event) => {
          event.preventDefault()
          if (busy || !name.trim()) return
          void act(async () => {
            const created = await onCreate(name.trim())
            setName('')
            setFilter('all')
            await load()
            setSelectedId(created.id)
            setNotice('Named draft saved. You can keep writing.')
          })
        }}
      >
        <label>
          Name a version to keep
          <input
            aria-label="Saved draft name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={100}
            placeholder="e.g. Act I · before the rewrite"
          />
        </label>
        <button
          type="submit"
          className="button button--primary"
          disabled={busy || !name.trim()}
        >
          {busy ? (
            <LoaderCircle size={15} className="spin" />
          ) : (
            <Plus size={15} />
          )}{' '}
          Save named draft
        </button>
      </form>
      {error && (
        <div className="dialog-message dialog-message--error" role="alert">
          {error}{' '}
          <button type="button" disabled={busy} onClick={() => void load()}>
            Retry history
          </button>
        </div>
      )}
      {notice && (
        <div className="dialog-message" role="status">
          <Check size={14} /> {notice}
        </div>
      )}
      <div className="draft-history-body">
        <aside className="draft-list" aria-label="Saved drafts">
          <select
            aria-label="Filter saved drafts"
            value={filter}
            onChange={(event) => setFilter(event.target.value as typeof filter)}
          >
            <option value="all">All saved drafts</option>
            <option value="named">Named drafts</option>
            <option value="automatic">Automatic recovery</option>
          </select>
          {loading ? (
            <p>Loading saved drafts…</p>
          ) : (
            revisions
              .filter((item) => filter === 'all' || item.kind === filter)
              .map((item) => (
                <button
                  type="button"
                  key={item.id}
                  aria-pressed={item.id === selectedId}
                  onClick={() => {
                    setSelectedId(item.id)
                    setError('')
                  }}
                  disabled={busy}
                >
                  <span>
                    {item.kind === 'named' ? (
                      <History size={13} />
                    ) : (
                      <span className="automatic-dot" />
                    )}
                    {item.name}
                  </span>
                  <small>
                    {date(item.createdAt)}
                    {item.actor
                      ? ' · Named by ' + item.actor.name
                      : item.sourceAuthor
                        ? ' · Draft by ' + item.sourceAuthor.name
                        : ''}
                  </small>
                  <small>
                    {item.sceneCount} scenes · {item.wordCount} words
                    {item.kind === 'automatic' ? ' · automatic' : ''}
                  </small>
                </button>
              ))
          )}
          {!loading && !revisions.length && (
            <p>
              No saved versions yet. Name this draft before you start a rewrite.
            </p>
          )}
        </aside>
        <section className="draft-comparison" aria-label="Draft comparison">
          {saved && comparison ? (
            <>
              <header>
                <span className="eyebrow">
                  COMPARED WITH YOUR CURRENT DRAFT
                </span>
                <h3>{saved.name}</h3>
                <p>
                  {date(saved.createdAt)} · {saved.wordCount} words
                </p>
              </header>
              <div className="draft-restore-actions">
                <button
                  type="button"
                  className="button button--primary"
                  disabled={busy}
                  onClick={() =>
                    void act(async () => {
                      await onRestore({
                        ...saved.project,
                        title: (saved.project.title + ' — restored').slice(
                          0,
                          120,
                        ),
                      })
                    })
                  }
                >
                  <Copy size={14} /> Restore as a new project
                </button>
                <button
                  type="button"
                  className="button"
                  onClick={() => void act(() => downloadFullProjectFile(saved.project, userId))}
                >
                  <Download size={14} /> Backup
                </button>
              </div>
              <p className="draft-restore-note">
                Restoring opens a separate project. This draft and its history
                stay on your story shelf.
              </p>
              <div className="draft-change-summary">
                <strong>{comparison.scenes.length}</strong>{' '}
                {comparison.scenes.length === 1 ? 'scene' : 'scenes'} changed{' '}
                <span>·</span> <strong>{comparison.project.length}</strong>{' '}
                project details changed
              </div>
              {!comparison.scenes.length && !comparison.project.length && (
                <div className="draft-identical">
                  <Check size={20} />
                  <p>
                    The writing, planning, and rules match this saved draft.
                  </p>
                </div>
              )}
              {comparison.project.length > 0 && (
                <details className="draft-scene-difference">
                  <summary>
                    Project & story bible{' '}
                    <span>{comparison.project.length} changes</span>
                  </summary>
                  {renderFields(comparison.project)}
                </details>
              )}
              {comparison.scenes.map((scene) => (
                <details className="draft-scene-difference" key={scene.nodeId}>
                  <summary>
                    {scene.title}
                    <span>{scene.kind}</span>
                  </summary>
                  {renderFields(scene.fields)}
                </details>
              ))}
              <p className="draft-layout-note">
                Comparison covers the manuscript, routes, and planning. Map
                positions are kept in the saved draft.
              </p>
              <div className="draft-delete">
                {confirmDelete ? (
                  <div className="inline-confirm" role="alert">
                    <strong>Remove “{saved.name}” from history?</strong>
                    <p>This saved version cannot be recovered after removal.</p>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(false)}
                      disabled={busy}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        void act(async () => {
                          await request('/' + saved.id, { method: 'DELETE' })
                          setSelectedId(null)
                          setSaved(null)
                          await load()
                          setNotice('Saved version removed.')
                        })
                      }
                    >
                      Remove saved version
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="danger-action"
                    disabled={busy}
                    onClick={() => setConfirmDelete(true)}
                  >
                    <Trash2 size={13} /> Remove this saved version
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="history-empty">
              <History size={30} />
              <h3>A little room to experiment.</h3>
              <p>
                {selectedId
                  ? 'Loading this saved draft…'
                  : 'Save a named draft, then compare or return to it whenever you need.'}
              </p>
            </div>
          )}
        </section>
      </div>
      <footer className="draft-history-footer">
        Named drafts are kept until you remove them (up to 50). The latest 20
        automatic recovery points are kept, at most one every five minutes of
        saving. History is stored on the server; project backups contain one
        draft.
      </footer>
    </WorkspaceDialog>
  )
}
